import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { ColumnsType } from "antd/es/table";
import { RootState } from "../../../app/store";
import {
  useApproveEdgeEnrollmentMutation,
  useGetEdgeEnrollmentsQuery,
  useRejectEdgeEnrollmentMutation,
} from "../../../redux/servers/serverAPI";
import type { EdgeEnrollment } from "../../../redux/servers/serverAPI";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";
import { apiErrorMessage } from "../../../utils/apiErrorMessage";
import useDocumentVisible from "../../../customHooks/useDocumentVisible";

const ENROLLMENT_POLL_MS = 3000;

/** Master-admin approval surface for claim codes shown on newly commissioned appliances. */
function PendingEdgeEnrollments() {
  const [t] = useTranslation("global");
  const { userLogged } = useSelector((state: RootState) => state.home);
  // Role only, matching LiftCloud's RequireMasterAdmin.
  const authorized = process.env.REACT_APP_ENVIRONMENT !== "OFFLINE" && isMasterAdmin(userLogged);
  const [busyID, setBusyID] = useState("");
  const visible = useDocumentVisible();

  const { data, error, isLoading } = useGetEdgeEnrollmentsQuery(undefined, {
    skip: !authorized,
    // 0 stops RTK polling; resume the interval once the tab is visible again.
    pollingInterval: visible ? ENROLLMENT_POLL_MS : 0,
  });
  const [approve] = useApproveEdgeEnrollmentMutation();
  const [reject] = useRejectEdgeEnrollmentMutation();

  if (!authorized) return null;

  const act = async (row: EdgeEnrollment, action: "approve" | "reject") => {
    setBusyID(row.id);
    try {
      if (action === "approve") await approve({ id: row.id }).unwrap();
      else await reject({ id: row.id }).unwrap();
      message.success(t(`edgeEnrollment.${action}Success`, { code: row.claim_code }));
    } catch (requestError) {
      message.error(apiErrorMessage(requestError, t("edgeEnrollment.actionError")));
    } finally {
      setBusyID("");
    }
  };

  const columns: ColumnsType<EdgeEnrollment> = [
    {
      title: t("edgeEnrollment.claimCode"),
      dataIndex: "claim_code",
      render: (value: string) => <Typography.Text code>{value}</Typography.Text>,
    },
    { title: t("edgeEnrollment.appliance"), dataIndex: "appliance_name" },
    { title: t("edgeEnrollment.organization"), dataIndex: "organization_name" },
    {
      title: t("edgeEnrollment.expires"),
      dataIndex: "expires_at",
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: t("edgeEnrollment.state"),
      dataIndex: "state",
      render: (value: EdgeEnrollment["state"]) => (
        <Tag color={value === "pending" ? "gold" : "blue"}>{t(`edgeEnrollment.${value}`)}</Tag>
      ),
    },
    {
      title: t("edgeEnrollment.actions"),
      key: "actions",
      render: (_value, row) => (
        <Space>
          {row.state === "pending" && (
            <Popconfirm
              title={t("edgeEnrollment.approveConfirm", { code: row.claim_code })}
              onConfirm={() => act(row, "approve")}
            >
              <Button type="primary" loading={busyID === row.id}>
                {t("edgeEnrollment.approve")}
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title={t(
              `edgeEnrollment.${row.state === "approved" ? "revokeConfirm" : "rejectConfirm"}`,
              {
                code: row.claim_code,
              },
            )}
            onConfirm={() => act(row, "reject")}
          >
            <Button danger loading={busyID === row.id}>
              {t(row.state === "approved" ? "edgeEnrollment.revoke" : "edgeEnrollment.reject")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title={t("edgeEnrollment.title")} style={{ margin: "0 12px 16px" }}>
      <Typography.Paragraph type="secondary">
        {t("edgeEnrollment.description")}
      </Typography.Paragraph>
      {error && (
        <Alert
          type="error"
          showIcon
          message={apiErrorMessage(error, t("edgeEnrollment.loadError"))}
          style={{ marginBottom: 12 }}
        />
      )}
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={false}
        locale={{
          emptyText: (
            <Empty description={t("edgeEnrollment.empty")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ),
        }}
      />
    </Card>
  );
}

export default PendingEdgeEnrollments;
